import React, { useMemo } from "react";
import PropTypes from "prop-types";

import TruncatedWithTooltip from "../../../../TruncatedWithTooltip";
import {
  UICheckbox,
  UIStatusTag,
  UITable,
  UIUserInfo,
} from "../../../../ui";
import { getUserInitials } from "../../../../ui/UIUserInfo";
import { clx } from "../../../../../utils/Helpers";
import EventPayrollPaymentProof from "./EventPayrollPaymentProof";
import { PAYROLL_PAYMENT_STATUSES } from "../constants";
import { formatFinalPayroll } from "./formatFinalPayroll";

import "./_EventPayrollTable.scss";

const EventPayrollTable = ({
  rows,
  labels,
  lang,
  selectedIds,
  canAdd,
  canEdit,
  isEventCompleted,
  onSelectRow,
  onSelectAll,
  onAddProof,
  onViewProof,
  onRowClick,
}) => {
  const canSelect = canAdd || canEdit;
  const allSelected =
    rows.length > 0 && rows.every((row) => selectedIds.includes(row.id));
  const headerIndeterminate =
    rows.length > 0 && selectedIds.length > 0 && !allSelected;

  const columns = useMemo(() => {
    const textColumn = (column) => ({
      align: "left",
      headerAlign: "left",
      ...column,
    });

    const selectColumn = canSelect
      ? [
          {
            field: "select",
            headerName: "",
            sortable: false,
            width: 64,
            minWidth: 64,
            align: "center",
            headerAlign: "center",
            cellClassName: "event-payroll__select-cell",
            headerClassName: "event-payroll__select-header",
            renderHeader: () => (
              <span
                className="event-payroll__checkbox-control"
                onClick={(event) => event.stopPropagation()}
              >
                <UICheckbox
                  className="event-payroll__checkbox"
                  checked={allSelected}
                  indeterminate={headerIndeterminate}
                  onChange={onSelectAll}
                  disabled={!rows.length}
                  label=""
                />
              </span>
            ),
            renderCell: ({ row }) => (
              <span
                className="event-payroll__checkbox-control"
                onClick={(event) => event.stopPropagation()}
              >
                <UICheckbox
                  className="event-payroll__checkbox"
                  checked={selectedIds.includes(row.id)}
                  onChange={() => onSelectRow(row.id)}
                  label=""
                />
              </span>
            ),
          },
        ]
      : [];

    return [
      ...selectColumn,
      textColumn({
        field: "id",
        headerName: labels.columns.id,
        autoFlex: false,
        flex: 0,
        width: 120,
        minWidth: 120,
        maxWidth: 120,
        sortable: false,
        cellClassName: "event-payroll__id-cell",
        headerClassName: "event-payroll__id-header",
        renderCell: ({ row }) => (
          <span className="event-payroll__id-value MuiDataGrid-cellContent">
            #{row.id}
          </span>
        ),
      }),
      textColumn({
        field: "staff",
        headerName: labels.columns.staff,
        minWidth: 260,
        cellClassName: "event-payroll__staff-cell",
        headerClassName: "event-payroll__staff-header",
        sortable: false,
        renderCell: ({ row }) => {
          const name = lang === "ar" ? row.nameAr || row.name : row.name;

          return (
            <UIUserInfo
              name={
                <TruncatedWithTooltip title={name}>
                  <span className="event-payroll__staff-name MuiDataGrid-cellContent">
                    {name}
                  </span>
                </TruncatedWithTooltip>
              }
              image={row.image || undefined}
              initials={getUserInitials(name)}
              classNames={{ root: "event-payroll__staff" }}
            />
          );
        },
      }),
      textColumn({
        field: "role",
        headerName: labels.columns.role,
        minWidth: 130,
        sortable: false,
        renderCell: ({ row }) => (
          <span className="MuiDataGrid-cellContent">{row.role || "-"}</span>
        ),
      }),
      textColumn({
        field: "nationality",
        headerName: labels.columns.nationality,
        minWidth: 130,
        sortable: false,
        renderCell: ({ row }) => (
          <span className="MuiDataGrid-cellContent">
            {row.nationality || "-"}
          </span>
        ),
      }),
      textColumn({
        field: "finalPayroll",
        headerName: labels.columns.finalPayroll,
        minWidth: 168,
        sortable: false,
        renderCell: ({ row }) => (
          <span className="MuiDataGrid-cellContent">
            {formatFinalPayroll(row.finalPayroll)}
          </span>
        ),
      }),
      textColumn({
        field: "paymentStatus",
        headerName: labels.columns.paymentStatus,
        minWidth: 130,
        sortable: false,
        renderCell: ({ row }) => (
          <UIStatusTag
            status={
              row.paymentStatus === PAYROLL_PAYMENT_STATUSES.paid
                ? "completed"
                : "pending"
            }
            label={
              row.paymentStatus === PAYROLL_PAYMENT_STATUSES.paid
                ? labels.paymentStatuses.paid
                : labels.paymentStatuses.pending
            }
            preset="table"
          />
        ),
      }),
      textColumn({
        field: "paymentProof",
        headerName: labels.columns.paymentProof,
        minWidth: 180,
        sortable: false,
        renderCell: ({ row }) => (
          <EventPayrollPaymentProof
            fileName={row.paymentProof?.fileName}
            href={row.paymentProof?.url}
            addProofLabel={labels.addProof}
            canAdd={canAdd}
            disabled={!isEventCompleted || selectedIds.length > 1}
            onAddProof={() => onAddProof?.(row.id)}
            onViewProof={onViewProof}
          />
        ),
      }),
    ];
  }, [
    allSelected,
    canAdd,
    canSelect,
    isEventCompleted,
    labels.addProof,
    labels.columns,
    labels.paymentStatuses.paid,
    labels.paymentStatuses.pending,
    headerIndeterminate,
    lang,
    onAddProof,
    onViewProof,
    onSelectAll,
    onSelectRow,
    rows.length,
    selectedIds,
  ]);

  const shouldIgnorePayrollRowNavigate = (event) =>
    event?.target?.closest?.(
      ".event-payroll__checkbox-control, button, a, input, .MuiCheckbox-root",
    );

  // The select column has its own padding around the checkbox; clicking
  // that padding still lands on the "select" cell, so handle toggling here
  // rather than relying solely on the checkbox's own hit area.
  const handlePayrollCellClick = (params, event) => {
    if (params.field === "select") {
      onSelectRow(params.row.id);
      return;
    }
    if (!onRowClick || shouldIgnorePayrollRowNavigate(event)) return;
    onRowClick(params.row);
  };

  return (
    <div
      className={clx(
        "event-payroll__table",
        onRowClick && "event-payroll__table--clickable-rows",
      )}
    >
      <UITable
        rows={rows}
        columns={columns}
        contentSized
        striped
        rowHeight={72}
        hideFooter
        disableColumnMenu
        disableSelectionOnClick
        getRowId={(row) => row.id}
        onCellClick={handlePayrollCellClick}
      />
    </div>
  );
};

EventPayrollTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object),
  labels: PropTypes.object.isRequired,
  lang: PropTypes.string.isRequired,
  selectedIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  canAdd: PropTypes.bool,
  canEdit: PropTypes.bool,
  isEventCompleted: PropTypes.bool,
  onSelectRow: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onAddProof: PropTypes.func,
  onViewProof: PropTypes.func,
  onRowClick: PropTypes.func,
};

EventPayrollTable.defaultProps = {
  rows: [],
  selectedIds: [],
  canAdd: true,
  canEdit: true,
  isEventCompleted: false,
  onAddProof: undefined,
  onViewProof: undefined,
  onRowClick: undefined,
};

export default EventPayrollTable;
